/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import EmailEditor from 'cio/components/action-composer/types/email/text-and-code/editor';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { isEmpty, isNone, isPresent } from '@ember/utils';
import { restartableTask, task, timeout } from 'ember-concurrency';
import * as Sentry from '@sentry/ember';
import window from 'ember-window-mock';
import BeeService, { BEE_ERROR, LOADED_BEE_TEMPLATE, Template } from 'cio/services/bee';
import { BEE_INSERT_LIQUID } from 'cio/services/event-bus';
import EmptyTemplate from 'cio/data/bee/empty-template';
import RavenService from 'cio/services/raven';
import AccountService from 'cio/services/account';
import AnalyticsService from 'cio/services/analytics';
import ApiService from 'cio/services/api';
import { EventsType } from 'cio/utils/analytics/events';
import SavedRowModel from 'cio/models/saved-row';
import DS from 'ember-data';
import { ONE_MINUTE_IN_SECONDS } from 'cio/utils/time-constants';
// eslint-disable-next-line @typescript-eslint/naming-convention
import jwt_decode from 'jwt-decode';
import CategoryModel from 'cio/models/category';
import Ember from 'ember';
import { unixNowSeconds } from 'cio/utils/time';
import { BeeCallbackResponse, BeeResponseCode } from 'cio/utils/bee-callback';
import { Editor } from 'cio/services/editor';
import ArrayProxy from '@ember/array/proxy';
import { getHostForDataCenter } from 'cio/utils/data-centers';
import AlertsService from 'cio/services/alerts';
import isDestroyed from 'cio/utils/is-destroyed';
import { createAssetRecord } from 'cio/utils/asset-library';

type PreviewError = {
  name: string;
  value: string;
};

enum BeeCommentChangeType {
  NewComment = 'NEW_COMMENT',
  CommentEdited = 'COMMENT_EDITED',
  CommentDeleted = 'COMMENT_DELETED',
  CommentThreadResolved = 'COMMENT_THREAD_RESOLVED',
  CommentThreatReopened = 'COMMENT_THREAD_REOPENED',
}

enum SavedRowTrackingEvent {
  Started = 'saved_row_started',
  Canceled = 'saved_row_canceled',
  Inserted = 'saved_row_inserted',
}

type BeeCommentChange = {
  type: BeeCommentChangeType;
  payload: {
    commentId: string;
    comment?: Record<string, any>;
  };
};

interface Token {
  environment_id: number;
  exp: number;
  iat: number;
}

const TOKEN_EXPIRATION_POLLING_INTERVAL = 5000;
const TOKEN_REFRESH_ALLOWANCE = ONE_MINUTE_IN_SECONDS;
const BEE_REQUESTS_FAILURE_THRESHOLD = 3;

export default class DragAndDropEditor extends EmailEditor {
  @service account!: AccountService;
  @service alerts!: AlertsService;
  @service api!: ApiService;
  @service bee!: BeeService;
  @service raven!: RavenService;
  @service analytics!: AnalyticsService;
  @service store!: DS.Store;

  @tracked hasSetupEditor = false;
  @tracked isHeadersExpanded = false;
  @tracked showBeeErrorModal = false;
  @tracked isShowingLiquidModal = false;
  @tracked isShowingSaveRowModal = false;
  @tracked isShowingManageSavedRowsModal = false;
  @tracked showAssetLibraryModal = false;
  @tracked tokenString?: string;
  @tracked rowData?: any = undefined;
  @tracked existingSavedRow?: SavedRowModel;
  @tracked categories?: ArrayProxy<CategoryModel> = undefined;
  @tracked failedBeeRequestCount = 0;
  @tracked showBeeRequestErrorsBanner = false;
  @tracked onAssetLibraryItemResolve: ((assetData: any) => void) | null = null;
  @tracked onAssetLibraryImageReject: (() => void) | null = null;
  @tracked filterAssetLibraryPdfs = true;

  get beeInstance() {
    return this.bee.getCurrentInstance()?.instance;
  }

  get externalRowBaseUrl() {
    return `${this.host}/v1/environments/${this.environment.current.id}/saved_rows_bee`;
  }

  get allCustomRowsCategoryConfig() {
    return {
      name: 'All custom rows',
      value: `${this.externalRowBaseUrl}?category_id=all`,
    };
  }

  get decodedToken(): Token | undefined {
    return this.tokenString ? jwt_decode(this.tokenString) : undefined;
  }

  get tokenExpiresAt() {
    return this.decodedToken?.exp;
  }

  get tokenWillExpireSoon() {
    if (this.tokenExpiresAt) {
      let timeRemaining =
        this.tokenExpiresAt - unixNowSeconds() - TOKEN_REFRESH_ALLOWANCE - TOKEN_EXPIRATION_POLLING_INTERVAL / 1000;
      return timeRemaining <= 0;
    }
    return undefined;
  }

  get host() {
    return getHostForDataCenter(this.account.current?.dataCenter) ?? '';
  }

  get beeRequestsFailureThresholdReached() {
    return this.failedBeeRequestCount >= BEE_REQUESTS_FAILURE_THRESHOLD;
  }

  @computed('isHeadersExpanded')
  get headersClasses() {
    if (this.isHeadersExpanded) {
      return 'fly-form-group fly-form-group--seamless';
    } else {
      return 'border-bottom-gray-light';
    }
  }

  @computed('args.errors.{bcc,from,reply_to,to,subject,preheader_text}')
  get nonContentErrors() {
    let { errors } = this.args;

    if (isNone(errors)) {
      return {};
    }

    let keys = ['bcc', 'from', 'reply_to', 'to', 'subject', 'preheader_text'];

    return Object.keys(errors).reduce(
      (nonContentErrors, key) => {
        if (keys.includes(key)) {
          nonContentErrors[key] = errors[key];
        }

        return nonContentErrors;
      },
      {} as Record<string, any>,
    );
  }

  @computed('args.errors.headers')
  get hasHeadersErrors() {
    let errors = this.args.errors?.headers;

    if (isNone(errors)) {
      return false;
    }

    return errors.any((error: PreviewError) => {
      return !isEmpty(error.name) || !isEmpty(error.value);
    });
  }

  @computed('hasHeadersErrors', 'nonContentErrors')
  get hasNonContentErrors() {
    let errors = this.nonContentErrors;

    if (this.hasHeadersErrors) {
      return true;
    }

    if (isNone(errors)) {
      return false;
    }

    return Object.keys(errors).any((key) => !isEmpty(errors[key]));
  }

  constructor(owner: unknown, args: DragAndDropEditor['args']) {
    super(owner, args);

    this.bee.on(BEE_ERROR, () => {
      if (this.isDestroyed) {
        return;
      }

      this.showBeeErrorModal = true;
    });

    this.openLiquidModal = this.openLiquidModal.bind(this);

    if (!Ember.testing) {
      this.pollForTokenExpiration.perform();
    }
  }

  @action
  setupBeeIfNotSet() {
    if (!this.hasSetupEditor && this.args.hasLoadedMergeData) {
      this.setupBeeEditorTask.perform();
    }
  }

  @action
  refreshBeeOnTemplateChange() {
    let editor = this.bee.getCurrentInstance();
    if (editor != null) {
      this.bee.prepareToUpdateTemplate(true);
      editor.instance.load(this._getInitialContent());
    }
  }

  willDestroy() {
    super.willDestroy();

    // isDestroyed check for tests:
    if (!isDestroyed(this.template)) {
      this.template.rollbackAttributes();
    }

    if (this.bee.has(LOADED_BEE_TEMPLATE)) {
      this.bee.off(LOADED_BEE_TEMPLATE, this.fetchMissingHTML);
    }

    this.bee.destroyEditor();
  }

  pollForTokenExpiration = task(async () => {
    await timeout(TOKEN_EXPIRATION_POLLING_INTERVAL);

    if (this.tokenWillExpireSoon) {
      let beeAuthToken = await this.requestBeeAuthToken.perform();
      this.bee.getCurrentInstance()?.instance?.loadConfig({
        customHeaders: [
          {
            name: 'AUTHORIZATION',
            value: beeAuthToken,
          },
        ],
      });
    }

    this.pollForTokenExpiration.perform();
  });

  requestBeeAuthToken = task(async () => {
    let { token } = await this.api.request('saved_rows_jwt');
    this.tokenString = token;
    return token;
  });

  getBeeConfig = task(async (options: { categories: ArrayProxy<CategoryModel> }) => {
    let defaultBeeConfig = this.bee.getDefaultConfig();
    let beeAuthToken = await this.requestBeeAuthToken.perform();
    let externalRowConfigObjects = [
      this.allCustomRowsCategoryConfig,
      {
        name: 'Uncategorized custom rows',
        value: `${this.externalRowBaseUrl}?category_id=0`,
      },
      ...options.categories.map((category) => ({
        name: category.name,
        value: `${this.externalRowBaseUrl}?category_id=${category.id}`,
      })),
    ];

    let config: any = {
      ...defaultBeeConfig,
      uid: `client-env-${this.environment.current.id}`,
      trackChanges: true,
      mergeTags: this.args.mergeTags || [],
      specialLinks: [
        {
          type: 'Frequently Used',
          title: 'Manage Subscription Preferences',
          label: 'Manage Subscription Preferences',
          link: '{% manage_subscription_preferences_url %}',
        },
        {
          type: 'Frequently Used',
          title: 'Unsubscribe',
          label: 'Unsubscribe',
          link: '{% unsubscribe_url %}',
        },
        {
          type: 'Frequently Used',
          title: 'View in Browser',
          label: 'View in Browser',
          link: '{% view_in_browser_url %}',
        },
      ],
      contentDialog: {
        saveRow: {
          handler: this.openSaveRowModal.bind(this),
        },
        mergeTags: {
          label: 'Add Liquid',
          handler: this.openLiquidModal.bind(this),
        },
        externalContentURLs: {
          label: 'Manage saved rows',
          handler: this.openManageSavedRowsModal.bind(this),
        },
      },
      customAttributes: {
        attributes: [
          {
            key: 'class',
            target: 'link',
          },
        ],
      },
      customHeaders: [
        {
          name: 'AUTHORIZATION',
          value: beeAuthToken,
        },
      ],
      rowsConfiguration: {
        emptyRows: true,
        externalContentURLs: externalRowConfigObjects,
      },
      onChange: (json: string, response: BeeCallbackResponse) => {
        if (response?.code === BeeResponseCode.RowDropped) {
          this.trackSavedRowEvent(SavedRowTrackingEvent.Inserted);
        }
        this._updateJSON(json);
      },
      onComment: ({ change, comments }: { change: BeeCommentChange; comments: Record<string, any> }) => {
        let oldJSON = JSON.parse(this.template.bodyJson);
        let newJSON = JSON.stringify({ ...oldJSON, comments });

        this._updateJSON(newJSON);
        this.trackCommentChange(change);
      },
    };

    // utilizes Asset Library for image selection with image blocks
    config.contentDialog.filePicker = {
      handler: (resolve: (imageData: any) => void, reject: () => void, args: any) => {
        this.onAssetLibraryItemResolve = resolve;
        this.onAssetLibraryImageReject = reject;

        if (args?.context === 'editor_images') {
          this.onUploadEditedImage(args.url);
        } else {
          this.filterAssetLibraryPdfs = !args?.context.endsWith('.link');
          this.showAssetLibraryModal = true;
        }
      },
    };

    return config;
  });

  setupBeeEditorTask = task(async () => {
    this.hasSetupEditor = true;

    let categories = await this.store.findAll('category');
    let beeConfig = await this.getBeeConfig.perform({ categories });

    this.categories = categories;

    return this.bee.getToken().then((token) => {
      let editor = this.bee.createEditor();
      editor.token = token;

      // If there is JSON and no HTML, which can only be true when starting
      // with predefined Bee templates, fetch the HTML manually.
      // The HTML needs to be fetched after Bee has loaded the JSON properly, so
      // we attach a callback to the LOADED_BEE_TEMPLATE event. Note that when
      // the .start() promise below resolves, the JSON hasn't quite been loaded
      // yet - we need to wait for the actual load event to fire.
      if (!isEmpty(this.template.bodyJson) && isEmpty(this.template.body)) {
        this.waitForBeeLoadedToFetchHTML();
      }

      return editor
        .start(beeConfig, this._getInitialContent())
        .then(() => {
          if (this.args.onBeeInit) {
            this.args.onBeeInit(editor);
          }
        })
        .catch((error: Error) => {
          Sentry.configureScope((scope) => {
            scope.setExtra('bee_uid', beeConfig.uid);
            this.raven.captureException(error);
          });
        });
    });
  });

  @action
  waitForBeeLoadedToFetchHTML() {
    this.bee.on(LOADED_BEE_TEMPLATE, this.fetchMissingHTML);
  }

  @action
  fetchMissingHTML() {
    this.fetchHTML.perform();

    if (this.bee.has(LOADED_BEE_TEMPLATE)) {
      this.bee.off(LOADED_BEE_TEMPLATE, this.fetchMissingHTML);
    }
  }

  @action
  trackCommentChange(change: BeeCommentChange) {
    let event: keyof EventsType['comments'];

    // Use a switch for exhaustive pattern matching
    switch (change.type) {
      case BeeCommentChangeType.NewComment:
        event = 'comment_added';
        break;
      case BeeCommentChangeType.CommentEdited:
        event = 'comment_edited';
        break;
      case BeeCommentChangeType.CommentDeleted:
        event = 'comment_deleted';
        break;
      case BeeCommentChangeType.CommentThreadResolved:
        event = 'comment_resolved';
        break;
      case BeeCommentChangeType.CommentThreatReopened:
        event = 'comment_reopened';
        break;
    }

    if (change.payload.comment?.parentCommentId) {
      event = 'comment_replied';
    }

    this.analytics.track('comments', event, { comment: change.payload });
  }

  @action
  trackSavedRowEvent(event: SavedRowTrackingEvent) {
    this.analytics.track('email', event, {
      message: this.args.editableEntity.model,
      messageType: 'email',
      template: this.template,
      editor: Editor.DRAG_AND_DROP,
    });
  }

  _updateJSON(json: string) {
    let existingJson = this.template.bodyJson;

    if (json !== existingJson) {
      this.template.set('bodyJson', json);
      this.fetchHTML.perform();
    }
  }

  fetchHTML = restartableTask(async () => {
    this.bee.prepareToUpdateTemplate();

    await this.getHTMLRequest();
    while (!this.beeRequestsFailureThresholdReached && this.failedBeeRequestCount > 0) {
      await this.getHTMLRequest();
    }
    if (this.beeRequestsFailureThresholdReached) {
      this.showBeeRequestErrorsBanner = true;
      throw new Error('Bee requests to fetch HTML failed after multiple attempts');
    }
  });

  private async getHTMLRequest() {
    try {
      let html = await this.bee.getHTML();
      this._updateHTML(html);
      this.failedBeeRequestCount = 0;
      this.showBeeRequestErrorsBanner = false;
    } catch {
      this.failedBeeRequestCount++;
    }
  }

  private _updateHTML(html: string) {
    let existingHtml = this.template.body;

    if (html !== existingHtml) {
      this.template.set('body', html ?? '');
    }
  }

  _getInitialContent() {
    // Look for any JSON that is already persisted for this template in
    // localStorage. It may be because the last session expired and the user
    // saved state and reloaded.
    //
    // If we have persisted JSON, we want to:
    //
    // (a) load the editor with it, and (b) make sure that we set that the
    // template's `bodyJson` property
    //
    // On its own, just (a) will also work but (b) is important so that the
    // template is marked dirty, the save button is enabled and the rest of the
    // behavior associated with save works.
    //
    // Otherwise, at load, the template is only saved in localStorage, not on
    // the backend but the save button is not enabled. If the user goes back to
    // the workflow, their changes are lost.
    let persistedJson = this.bee.getPersistedStateFor(this.template as Template);
    if (!isNone(persistedJson)) {
      this.template.set('bodyJson', persistedJson);
      this.bee.purgeStateFor(this.template as Template);
    }

    let json = this.template.bodyJson;

    if (isNone(json)) {
      return EmptyTemplate;
    }

    try {
      return JSON.parse(json);
    } catch (error) {
      // Failing to parse JSON data can lead to unexpected UI in the client, so
      // we should be able to look into it.
      this.raven.captureException(new Error('Unable to parse Bee template JSON'), {
        extra: {
          error: error.message,
          originalJson: json,
        },
      });

      return EmptyTemplate;
    }
  }

  @action
  toggleHeadersExpanded() {
    this.isHeadersExpanded = !this.isHeadersExpanded;
  }

  @action
  saveAndReload() {
    this.bee.persistStateFor(this.template as Template).then(() => {
      this.template.rollbackAttributes();
      this._reload();
    });
  }

  @action
  reload() {
    this._reload();
  }

  @action
  cancelLiquidModal() {
    this.eventBus.publish(BEE_INSERT_LIQUID, null);
  }

  @action
  updateCategories(category: CategoryModel) {
    let config = {
      rowsConfiguration: {
        externalContentURLs: [
          {
            name: category.name,
            value: `${this.host}/v1/environments/${this.environment.current.id}/saved_rows_bee?category_id=${category.id}`,
          },
        ],
      },
    };
    this.beeInstance?.loadConfig(config);
  }

  @action
  setTemplateFakeBcc() {
    if (!this.template.hasBcc) {
      this.template.set('fakeBcc', true);
    }
  }

  _reload() {
    window.location.reload();
  }

  saveRow!: (row: SavedRowModel) => void;
  cancelSaveRow!: () => void;
  private resetSavedRowState() {
    this.existingSavedRow = undefined;
    this.isShowingSaveRowModal = false;
    this.rowData = undefined;
  }
  private async openSaveRowModal(resolve: any, reject: any, rowData: any) {
    this.saveRow = async (row: SavedRowModel) => {
      let { name } = row;
      let categoryId = row.category?.get('id');

      this.rowData.metadata = {
        name,
        category_id: categoryId,
      };
      row.data = this.rowData;

      let savedRow = await row.save();

      this.rowData.metadata.saved_row_id = savedRow.id;
      resolve(this.rowData.metadata);
      this.resetSavedRowState();

      return savedRow;
    };
    this.cancelSaveRow = () => {
      this.resetSavedRowState();
      this.trackSavedRowEvent(SavedRowTrackingEvent.Canceled);
      return reject();
    };

    let savedRowId = rowData.metadata?.saved_row_id;

    if (savedRowId) {
      try {
        this.existingSavedRow = await this.store.findRecord('saved-row', savedRowId);
      } catch (reason) {
        let errors = reason.errors ?? [];
        let [firstError] = errors;

        // 404 errors are ignored and treated as if the user is creating a new row
        if (isPresent(firstError?.status) && firstError?.status !== '404') {
          // other errors indicate something has gone wrong with loading the record
          // saving/updating is unable to proceed in this state
          this.alerts.error(
            'savedRow.find.failure',
            'Unable to retrieve this row. Please try again or contact support.',
          );
          return reject();
        }
      }
    }

    this.trackSavedRowEvent(SavedRowTrackingEvent.Started);
    this.isShowingSaveRowModal = true;
    this.rowData = rowData;
  }

  closeManageSavedRowsModal!: () => void;
  private async openManageSavedRowsModal(resolve: any) {
    this.closeManageSavedRowsModal = () => {
      this.isShowingManageSavedRowsModal = false;
      resolve(this.allCustomRowsCategoryConfig);
    };

    this.isShowingManageSavedRowsModal = true;
  }

  onBeeInsertLiquid!: (liquidTextBlock: string) => void;
  private openLiquidModal(resolve: ({ name, value }: { name: string; value: string }) => unknown) {
    this.onBeeInsertLiquid = (liquidTextBlock) => {
      let value = liquidTextBlock || '';

      resolve({
        name: 'Liquid text block',
        value: value.replace(new RegExp('\n', 'g'), '<br />'),
      });

      this.closeLiquidModal();
    };

    this.showLiquidModal();
  }

  @action
  private showLiquidModal() {
    this.isShowingLiquidModal = true;
    this.eventBus.subscribe(BEE_INSERT_LIQUID, this, this.onBeeInsertLiquid);
  }

  @action
  private closeLiquidModal() {
    this.isShowingLiquidModal = false;
    if (this.onBeeInsertLiquid) {
      this.eventBus.unsubscribe(BEE_INSERT_LIQUID, this, this.onBeeInsertLiquid);
    }
  }

  @action
  async onUploadEditedImage(dataUrl: string) {
    try {
      // requires unique name to avoid erroring out on duplicate asset name
      let asset = await createAssetRecord(
        `${this.args.editableEntity.model.name}_image-edit_${new Date().getTime()}`,
        dataUrl,
        this.store,
      );
      this.onAssetLibraryItemResolve?.({ url: asset.path });
    } catch (ex) {
      this.alerts.error('assetLibrary.upload.beeImageEditor', 'Edited image could not be uploaded');
      this.onAssetLibraryImageReject?.();
    }
  }

  @action
  hideAssetLibraryModal() {
    this.showAssetLibraryModal = false;
    this.onAssetLibraryImageReject?.();
  }

  @action
  onSelectAssetLibraryItem(assetPath: string) {
    this.onAssetLibraryItemResolve?.({ url: assetPath });
    this.showAssetLibraryModal = false;
  }
}
